import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";
import { Bell, CalendarDays, Clock, Repeat, Sun, X } from "lucide-solid";
import { createMemo, For, Show } from "solid-js";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  buildRecurrenceRule,
  cn,
  combineLocalDateTime,
  computeReminderAt,
  deriveReminderOffsetMinutes,
  getDatePresets,
  JS_DAY_TO_WEEKDAY,
  type ParsedRecurrence,
  parseRecurrenceRule,
  type RecurrenceFreq,
  REMINDER_OFFSET_MINUTES,
  splitLocalDateTime,
  toLocalDateKey,
  type Weekday,
  WEEKDAYS_ORDER,
} from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { Calendar } from "./calendar";

export interface ScheduleValue {
  due_date: string | null;
  reminder_at: string | null;
  recurrence_rule: string | null;
}

interface SchedulePanelProps {
  value: () => ScheduleValue;
  onChange: (patch: Partial<ScheduleValue>) => void;
  onSave: () => void;
  onCancel: () => void;
}

const DEFAULT_TIME = "09:00";
const REFERENCE_YEAR = 2024;

const SECTION_LABEL =
  "px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const CHIP =
  "inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 h-9 text-sm font-medium border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary";
const CHIP_ON = "border-primary/30 bg-primary/10 text-primary";
const CHIP_OFF = "border-border/60 text-foreground/80 hover:bg-muted/15";

function selectClass(active: boolean): string {
  return cn(CHIP, active ? CHIP_ON : CHIP_OFF);
}

export function SchedulePanel(props: SchedulePanelProps) {
  const { t, locale } = useI18n();

  const dueIso = () => props.value().due_date;
  const parts = createMemo(() => splitLocalDateTime(dueIso()));
  const selectedDay = createMemo(() => {
    const iso = dueIso();
    return iso ? new Date(iso) : null;
  });
  const isAllDay = () => Boolean(parts().dateKey) && parts().time === null;

  /** The active reminder preset (minutes-before), `"custom"`, or `null` (none). */
  const currentOffset = (): number | "custom" | null => {
    const value = props.value();
    if (!value.reminder_at) {
      return null;
    }
    const derived = deriveReminderOffsetMinutes(value.due_date, value.reminder_at);
    const presetValues = Object.values(REMINDER_OFFSET_MINUTES) as number[];
    return derived !== null && presetValues.includes(derived) ? derived : "custom";
  };

  // ── Date ────────────────────────────────────────────────────────────────
  /** Rewrites due_date keeping the current time, and re-derives the reminder. */
  const applyDate = (date: Date | null) => {
    if (!date) {
      props.onChange({ due_date: null, reminder_at: null, recurrence_rule: null });
      return;
    }
    const dueIsoNext = combineLocalDateTime(toLocalDateKey(date), parts().time);
    const patch: Partial<ScheduleValue> = { due_date: dueIsoNext };
    // Keep a relative reminder pinned to the (new) due date.
    const offset = currentOffset();
    if (offset !== null && offset !== "custom") {
      patch.reminder_at = computeReminderAt(dueIsoNext, offset);
    }
    props.onChange(patch);
  };

  const presets = createMemo(() => {
    const { today, tomorrow, weekend, nextWeek } = getDatePresets();
    const weekdayShort = (d: Date) =>
      new Intl.DateTimeFormat(locale(), { weekday: "short" }).format(d);
    return [
      { key: "today", label: msg`Today`, date: today },
      { key: "tomorrow", label: msg`Tomorrow`, date: tomorrow },
      { key: "weekend", label: weekdayShort(weekend), date: weekend },
      { key: "nextWeek", label: msg`Next week`, date: nextWeek },
    ];
  });

  // ── Time ────────────────────────────────────────────────────────────────
  const setTime = (time: string | null) => {
    const key = parts().dateKey;
    if (!key) {
      return;
    }
    const dueIsoNext = combineLocalDateTime(key, time);
    const patch: Partial<ScheduleValue> = { due_date: dueIsoNext };
    const offset = currentOffset();
    if (offset !== null && offset !== "custom") {
      patch.reminder_at = computeReminderAt(dueIsoNext, offset);
    }
    props.onChange(patch);
  };

  const toggleAllDay = () => {
    setTime(isAllDay() ? DEFAULT_TIME : null);
  };

  // ── Reminder ──────────────────────────────────────────────────────────────
  const reminderOptions = createMemo(() => [
    { label: msg`None`, value: null as number | null },
    { label: msg`At time of due date`, value: REMINDER_OFFSET_MINUTES.AT_TIME },
    { label: msg`10 minutes before`, value: REMINDER_OFFSET_MINUTES.MIN_10 },
    { label: msg`30 minutes before`, value: REMINDER_OFFSET_MINUTES.MIN_30 },
    { label: msg`1 hour before`, value: REMINDER_OFFSET_MINUTES.HOUR_1 },
    { label: msg`1 day before`, value: REMINDER_OFFSET_MINUTES.DAY_1 },
    { label: msg`1 week before`, value: REMINDER_OFFSET_MINUTES.WEEK_1 },
  ]);

  const setReminderOffset = (offset: number | null) => {
    const due = dueIso();
    if (offset === null || !due) {
      props.onChange({ reminder_at: null });
      return;
    }
    props.onChange({ reminder_at: computeReminderAt(due, offset) });
  };

  // ── Recurrence ────────────────────────────────────────────────────────────
  const recurrence = createMemo<ParsedRecurrence | null>(() =>
    parseRecurrenceRule(props.value().recurrence_rule),
  );

  const emitRecurrence = (next: ParsedRecurrence | null) => {
    props.onChange({ recurrence_rule: next ? buildRecurrenceRule(next) : null });
  };

  const setFreq = (freq: RecurrenceFreq | null) => {
    if (!freq) {
      emitRecurrence(null);
      return;
    }
    const current = recurrence();
    let byDays = freq === "WEEKLY" ? (current?.byDays ?? []) : [];
    if (freq === "WEEKLY" && byDays.length === 0) {
      const date = selectedDay();
      if (date) {
        const dayIdx = date.getDay();
        const wkday = JS_DAY_TO_WEEKDAY[dayIdx];
        if (wkday) {
          byDays = [wkday];
        }
      } else {
        const todayWkday = JS_DAY_TO_WEEKDAY[new Date().getDay()];
        if (todayWkday) {
          byDays = [todayWkday];
        }
      }
    }
    emitRecurrence({
      freq,
      interval: current?.interval ?? 1,
      byDays,
      until: current?.until ?? null,
    });
  };

  const toggleWeekday = (day: Weekday) => {
    const current = recurrence();
    if (!current) {
      return;
    }
    const byDays = current.byDays.includes(day)
      ? current.byDays.filter((d) => d !== day)
      : [...current.byDays, day];
    emitRecurrence({ ...current, byDays });
  };

  const setInterval = (interval: number) => {
    const current = recurrence();
    if (!current) {
      return;
    }
    emitRecurrence({ ...current, interval: Math.max(1, interval) });
  };

  const setUntil = (untilIso: string | null) => {
    const current = recurrence();
    if (!current) {
      return;
    }
    emitRecurrence({ ...current, until: untilIso });
  };

  const freqOptions: { label: MessageDescriptor; value: RecurrenceFreq | null }[] = [
    { label: msg`Does not repeat`, value: null },
    { label: msg`Daily`, value: "DAILY" },
    { label: msg`Weekly`, value: "WEEKLY" },
    { label: msg`Monthly`, value: "MONTHLY" },
    { label: msg`Yearly`, value: "YEARLY" },
  ];

  const weekdayLabel = (day: Weekday): string => {
    const index = WEEKDAYS_ORDER.indexOf(day);
    const reference = new Date(REFERENCE_YEAR, 0, 1 + index); // Mon 1 Jan 2024 + index
    return new Intl.DateTimeFormat(locale(), { weekday: "narrow" }).format(reference);
  };

  const untilDate = createMemo((): Date | null => {
    const until = recurrence()?.until;
    return until ? new Date(until) : null;
  });

  const untilFormatted = () => {
    const until = recurrence()?.until;
    if (!until) {
      return t(msg`Never`);
    }
    return new Intl.DateTimeFormat(locale(), {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(new Date(until));
  };

  return (
    <div class="flex flex-col gap-4 relative">
      {/* Absolute close button in the top-right corner */}
      <button
        type="button"
        aria-label={t(msg`Close`)}
        class="absolute -right-1 -top-1 rounded-full p-1.5 text-muted-foreground/60 hover:text-foreground hover:bg-muted/15 cursor-pointer active:scale-95 transition-all flex items-center justify-center shrink-0 z-10"
        onClick={() => {
          props.onCancel();
        }}
      >
        <X class="h-4 w-4" />
      </button>

      {/* Quick presets */}
      <div class="flex flex-col gap-1.5 pr-8">
        <span class={SECTION_LABEL}>{t(msg`Due date`)}</span>
        <div class="flex flex-wrap gap-1.5 px-1">
          <For each={presets()}>
            {(preset) => (
              <button
                type="button"
                class={selectClass(false)}
                onClick={() => {
                  applyDate(preset.date);
                }}
              >
                <CalendarDays class="h-3.5 w-3.5" />
                <span class="capitalize">{t(preset.label)}</span>
              </button>
            )}
          </For>
          <Show when={dueIso()}>
            <button
              type="button"
              class={cn(
                CHIP,
                "border-border/60 text-muted-foreground hover:bg-destructive/10 hover:text-destructive",
              )}
              onClick={() => {
                applyDate(null);
              }}
            >
              <X class="h-3.5 w-3.5" />
              {t(msg`Clear`)}
            </button>
          </Show>
        </div>
      </div>

      <Calendar selected={selectedDay} onSelect={applyDate} />

      {/* Time / all-day */}
      <Show when={dueIso()}>
        <div class="flex flex-col gap-2 border-t border-border/60 pt-3">
          <div class="flex items-center justify-between px-1">
            <span class="flex items-center gap-2 text-sm font-medium text-foreground/90">
              <Sun class="h-4 w-4 text-muted-foreground" />
              {t(msg`All day`)}
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={isAllDay()}
              aria-label={t(msg`All day`)}
              class={cn(
                "relative h-6 w-11 rounded-full p-0 shrink-0 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                isAllDay() ? "bg-primary" : "bg-muted",
              )}
              onClick={toggleAllDay}
            >
              <span
                class={cn(
                  "absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform",
                  isAllDay() ? "translate-x-5" : "translate-x-0",
                )}
              />
            </button>
          </div>
          <Show when={!isAllDay()}>
            <label class="flex items-center gap-2 px-1 text-sm">
              <Clock class="h-4 w-4 text-muted-foreground" />
              <span class="text-foreground/80">{t(msg`Time`)}</span>
              <input
                type="time"
                value={parts().time ?? DEFAULT_TIME}
                class="ml-auto rounded-lg border border-border/60 bg-background px-2.5 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                onChange={(e) => {
                  setTime(e.currentTarget.value || DEFAULT_TIME);
                }}
              />
            </label>
          </Show>
        </div>
      </Show>

      {/* Reminder */}
      <Show when={dueIso()}>
        <div class="flex flex-col gap-1.5 border-t border-border/60 pt-3">
          <span class={cn(SECTION_LABEL, "flex items-center gap-1.5")}>
            <Bell class="h-3 w-3" />
            {t(msg`Notify me`)}
          </span>
          <div class="flex flex-wrap gap-1.5 px-1">
            <For each={reminderOptions()}>
              {(option) => (
                <button
                  type="button"
                  class={selectClass(currentOffset() === option.value)}
                  onClick={() => {
                    setReminderOffset(option.value);
                  }}
                >
                  {t(option.label)}
                </button>
              )}
            </For>
            <Show when={currentOffset() === "custom"}>
              <button type="button" class={selectClass(true)}>
                {t(msg`Custom`)}
              </button>
            </Show>
          </div>
        </div>
      </Show>

      {/* Recurrence */}
      <Show when={dueIso()}>
        <div class="flex flex-col gap-2 border-t border-border/60 pt-3">
          <span class={cn(SECTION_LABEL, "flex items-center gap-1.5")}>
            <Repeat class="h-3 w-3" />
            {t(msg`Repeat`)}
          </span>
          <div class="flex flex-wrap gap-1.5 px-1">
            <For each={freqOptions}>
              {(option) => (
                <button
                  type="button"
                  class={selectClass((recurrence()?.freq ?? null) === option.value)}
                  onClick={() => {
                    setFreq(option.value);
                  }}
                >
                  {t(option.label)}
                </button>
              )}
            </For>
          </div>

          {/* Weekly weekday picker */}
          <Show when={recurrence()?.freq === "WEEKLY"}>
            <div class="flex gap-1 px-1">
              <For each={WEEKDAYS_ORDER}>
                {(day) => (
                  <button
                    type="button"
                    aria-pressed={recurrence()?.byDays.includes(day)}
                    class={cn(
                      "flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold uppercase transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
                      recurrence()?.byDays.includes(day)
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted/40 text-foreground/70 hover:bg-muted/60",
                    )}
                    onClick={() => {
                      toggleWeekday(day);
                    }}
                  >
                    {weekdayLabel(day)}
                  </button>
                )}
              </For>
            </div>
          </Show>

          {/* Custom interval + end date */}
          <Show when={recurrence()}>
            <div class="flex flex-col gap-2 px-1 pt-1">
              <label class="flex items-center gap-2 text-sm">
                <span class="text-foreground/80">{t(msg`Every`)}</span>
                <input
                  type="number"
                  min="1"
                  value={recurrence()?.interval ?? 1}
                  class="w-16 rounded-lg border border-border/60 bg-background px-2 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  onChange={(e) => {
                    setInterval(Number(e.currentTarget.value));
                  }}
                />
                <span class="text-muted-foreground">{recurrenceUnit(recurrence()?.freq, t)}</span>
              </label>
              <div class="flex items-center gap-2 text-sm w-full">
                <span class="text-foreground/80">{t(msg`Ends on`)}</span>
                <Popover placement="bottom-end">
                  <PopoverTrigger
                    as="button"
                    type="button"
                    class="ml-auto rounded-lg border border-border/60 bg-background px-2.5 py-1.5 text-sm font-medium hover:bg-muted/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary flex items-center gap-1.5"
                  >
                    <CalendarDays class="h-3.5 w-3.5 text-muted-foreground" />
                    <span>{untilFormatted()}</span>
                  </PopoverTrigger>
                  <PopoverContent class="w-[340px] z-[250] p-4">
                    <Calendar
                      selected={untilDate}
                      onSelect={(date) => {
                        setUntil(combineLocalDateTime(toLocalDateKey(date), null));
                      }}
                    />
                  </PopoverContent>
                </Popover>
                <Show when={recurrence()?.until}>
                  <button
                    type="button"
                    class="rounded-md p-1 text-muted-foreground hover:bg-muted/20"
                    aria-label={t(msg`Clear`)}
                    onClick={() => {
                      setUntil(null);
                    }}
                  >
                    <X class="h-3.5 w-3.5" />
                  </button>
                </Show>
              </div>
            </div>
          </Show>
        </div>
      </Show>

      <div class="flex items-center justify-end gap-2 border-t border-border/60 pt-3 mt-1">
        <button
          type="button"
          class="rounded-lg border border-border/60 px-4 py-2 text-sm font-semibold text-foreground/80 hover:bg-muted/15 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer"
          onClick={() => {
            props.onCancel();
          }}
        >
          {t(msg`Cancel`)}
        </button>
        <button
          type="button"
          class="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary cursor-pointer"
          onClick={() => {
            props.onSave();
          }}
        >
          {t(msg`Save`)}
        </button>
      </div>
    </div>
  );
}

function recurrenceUnit(
  freq: RecurrenceFreq | undefined,
  t: (message: string | MessageDescriptor) => string,
): string {
  if (!freq) {
    return "";
  }
  const units: Record<RecurrenceFreq, string> = {
    DAILY: t(msg`days`),
    WEEKLY: t(msg`weeks`),
    MONTHLY: t(msg`months`),
    YEARLY: t(msg`years`),
  };
  return units[freq];
}
